/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
/* eslint-disable jsx-a11y/media-has-caption */
import { Pause, Play } from '@carbon/react/icons';
import { Button, Loading, TextArea, Tile } from 'carbon-components-react';
import { ElevenLabsClient } from 'elevenlabs';
import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useAppSelector } from '@ibm-watson/shared/store';
import { useDelayDebounce } from '../../../../actions/hooks/useDelayDebounce';
import { useFormHelpers } from '../../../hooks/useFormHelpers';
import {
  selectElevenLabsModels,
  selectElevenLabsModelsErrors,
  selectElevenLabsVoices
} from '../../../store/voice/selectors';
import { convertAudioBlobToVisualizationData } from '../phone-helpers';
import { FIELDS } from '../schema';
import { AudioWave } from './AudioWave';
import { TextToSpeech as messages } from './messages';
import { TextToSpeechVoiceTile } from './TextToSpeechVoiceTile';

export const AudioVisualizerElevenLabs = () => {
  const { formatMessage: t } = useIntl();

  const formMethods = useFormContext();
  const { getFieldValue } = useFormHelpers(formMethods);

  const models = useAppSelector(selectElevenLabsModels);
  const apiKeyError = useAppSelector(selectElevenLabsModelsErrors);
  const voices = useAppSelector(selectElevenLabsVoices);

  // audio elements
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioPlaying, setAudioPlaying] = useState(null);
  const [audioPaused, setAudioPaused] = useState(false);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const [audioData, setAudioData] = useState(null);
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioError, setAudioError] = useState(false);

  // data
  const [isParamsUpdated, setIsParamsUpdated] = useState(true);
  const [selectedVoice, setSelectedVoice] = useState(undefined);
  const [selectedModel, setSelectedModel] = useState(undefined);
  const [textAreaValue, setTextAreaValue] = useState(
    t(messages.visualizerTextAreaPlaceHolder)
  );

  const audioRef = useRef(null);
  // This React ref is used as a mutable flag to determine whether audio playback (or a loading process) has been cancelled or interrupted, without triggering a re-render of the component.
  const isloadingCancelledAudioRef = useRef(false);
  const audioWaveContainerRef = useRef(null);

  // Get the config values from the fields
  const ttsElevenLabsConfigSpeed = getFieldValue(
    FIELDS.TTS.ELEVENLABS_VOICE_SETTINGS.SPEED
  );
  const ttsElevenLabsConfigStability = getFieldValue(
    FIELDS.TTS.ELEVENLABS_VOICE_SETTINGS.STABILITY
  );
  const ttsElevenLabsConfigStyle = getFieldValue(
    FIELDS.TTS.ELEVENLABS_VOICE_SETTINGS.STYLE
  );
  const ttsElevenLabsConfigSimilarity = getFieldValue(
    FIELDS.TTS.ELEVENLABS_VOICE_SETTINGS.SIMILARITY_BOOST
  );
  const ttsElevenLabsConfigSpeakerBoost = getFieldValue(
    FIELDS.TTS.ELEVENLABS_VOICE_SETTINGS.USE_SPEAKER_BOOST
  );
  const debouncedElevenceAPIKey = useDelayDebounce(
    getFieldValue(FIELDS.TTS.ELEVENLABS_APIKEY),
    1000
  );

  const ttsElevenLabsVoiceID = getFieldValue(FIELDS.TTS.VOICE_ID);
  const modelId = getFieldValue(FIELDS.TTS.MODEL_ID);

  useEffect(() => {
    if (voices?.length && ttsElevenLabsVoiceID) {
      const selVoice = voices.find(vo => vo.voice_id === ttsElevenLabsVoiceID);
      setSelectedVoice(selVoice);
    }
  }, [ttsElevenLabsVoiceID, voices]);

  useEffect(() => {
    if (models?.length && modelId) {
      const selModel = models.find(model => model.model_id === modelId);
      setSelectedModel(selModel);
    }
  }, [models, modelId]);

  useEffect(() => {
    return () => {
      var audioElement = document.getElementById(
        'TextToSpeech__audio-11Labs'
      ) as HTMLAudioElement;
      if (audioElement) {
        isloadingCancelledAudioRef.current = true;
        audioElement.pause();
        audioElement.src = '';
        audioElement.load();
      }
    };
  }, []);

  useEffect(() => {
    if (!audioRef.current) {
      return;
    }
    if (
      navigator.userAgent.indexOf('Safari') !== -1 &&
      navigator.userAgent.indexOf('Chrome') === -1
    ) {
      audioRef.current.preload = 'metadata';
    }

    audioRef.current.addEventListener('play', () => {
      if (navigator.userAgent.indexOf('Safari') === -1) {
        setAudioLoading(false);
        setAudioPaused(false);
        setAudioPlaying(true);
      }
    });

    audioRef.current.addEventListener('playing', () => {
      if (navigator.userAgent.indexOf('Safari') !== -1) {
        setAudioLoading(false);
        setAudioPaused(false);
        setAudioPlaying(true);
      }
    });

    audioRef.current.addEventListener('loadeddata', () => {
      setAudioLoading(false);
      setAudioPaused(false);
      setAudioPlaying(false);
      setAudioError(false);
    });

    audioRef.current.addEventListener('ended', () => {
      setAudioPlaying(false);
    });

    audioRef.current.addEventListener('error', () => {
      if (!isloadingCancelledAudioRef.current) {
        setAudioError(true);
      }
    });

    audioRef.current.addEventListener('loadedmetadata', () => {
      if (navigator.userAgent.indexOf('Safari') !== -1) {
        // safari works best when playing here
        if (audioRef.current && !audioRef.current.autoplay) {
          setAudioLoading(false);
        }
      }
    });
  }, [models]);

  const buildReqUrl = async (autoplay: boolean, audioCtx: AudioContext) => {
    if (!audioRef.current || !debouncedElevenceAPIKey || apiKeyError) {
      return;
    }

    try {
      setAudioLoading(true);
      audioRef.current.pause();
      audioRef.current.removeAttribute('src');

      const text = textAreaValue.replace(/[\r\n]/gm, ' ');

      const client = new ElevenLabsClient({
        apiKey: debouncedElevenceAPIKey
      });

      let audioBlob: Blob;

      const response = await client.textToSpeech.convertAsStream(
        ttsElevenLabsVoiceID,
        {
          text: text,
          model_id: modelId,
          voice_settings: {
            stability: ttsElevenLabsConfigStability,
            similarity_boost: ttsElevenLabsConfigSimilarity,
            style: ttsElevenLabsConfigStyle,
            use_speaker_boost: ttsElevenLabsConfigSpeakerBoost,
            speed: ttsElevenLabsConfigSpeed
          }
        }
      );

      // Check if the stream is a Web ReadableStream
      if (response && response instanceof ReadableStream) {
        const reader = response.getReader();
        const chunks = [];
        let done = false;

        while (!done) {
          const { value, done: chunkDone } = await reader.read();
          if (value) {
            chunks.push(value);
          }
          done = chunkDone;
        }

        const audioBuffer = new Uint8Array(
          chunks.reduce((acc, chunk) => acc + chunk.length, 0)
        );
        let offset = 0;
        for (const chunk of chunks) {
          audioBuffer.set(chunk, offset);
          offset += chunk.length;
        }

        audioBlob = new Blob([audioBuffer], { type: 'audio/mp3' });
      } else {
        throw new Error('Unexpected stream type from ElevenLabs');
      }

      if (!isloadingCancelledAudioRef.current && audioBlob) {
        const audioUrl = URL.createObjectURL(audioBlob);

        const { duration, reducedFloatArray } =
          await convertAudioBlobToVisualizationData(audioBlob, audioCtx);

        setAudioData(reducedFloatArray);
        setAudioDuration(duration);
        if (audioRef.current) {
          audioRef.current.setAttribute('src', audioUrl);
          audioRef.current.setAttribute('type', 'audio/mp3');
          audioRef.current.controls = false;

          if (autoplay) {
            audioRef.current.autoplay = true;
          }
        }
      }
    } catch (e) {
      console.error(e);
      if (!isloadingCancelledAudioRef.current) {
        setAudioLoading(false);
      }
    }
  };

  // direct GET request for streaming audio from TTS
  const playAudioDirect = async () => {
    if (!textAreaValue || !debouncedElevenceAPIKey) {
      return;
    }

    if (!audioCtxRef.current || audioCtxRef.current.state === 'closed') {
      audioCtxRef.current = new AudioContext();
    }

    if (isParamsUpdated) {
      if (audioRef.current) {
        setAudioPaused(false);
        setAudioPlaying(false);
      }
      setIsParamsUpdated(false);
      await buildReqUrl(true, audioCtxRef.current);
      return;
    }
    if (!audioLoading) {
      if (audioPlaying && !audioPaused) {
        audioRef.current.pause();
        setAudioPaused(true);
        return;
      }
      if (audioPaused) {
        audioRef.current.play();
        setAudioPaused(false);
        return;
      }
      if (!audioPlaying) {
        if (
          navigator.userAgent.indexOf('Safari') !== -1 &&
          navigator.userAgent.indexOf('Chrome') === -1
        ) {
          await buildReqUrl(true, audioCtxRef.current);
        } else {
          audioRef.current.currentTime = 0;
          audioRef.current.play();
        }
      }
    }
  };

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    setAudioPlaying(false);
    setAudioError(false);
    setIsParamsUpdated(true);
  }, [ttsElevenLabsVoiceID]);

  useEffect(() => {
    setAudioError(false);
    if (!audioRef.current || !ttsElevenLabsVoiceID || !modelId) {
      return;
    }

    if (!audioCtxRef.current || audioCtxRef.current.state === 'closed') {
      // AudioContext must be created on user interaction, so skip here
      return;
    }

    if (!audioRef.current.src && !audioLoading) {
      buildReqUrl(false, audioCtxRef.current);
      setIsParamsUpdated(false);
    } else if (audioRef.current.src) {
      setIsParamsUpdated(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ttsElevenLabsVoiceID,
    textAreaValue,
    ttsElevenLabsConfigSpeed,
    ttsElevenLabsConfigStability,
    ttsElevenLabsConfigStyle,
    ttsElevenLabsConfigSimilarity,
    ttsElevenLabsConfigSpeakerBoost
  ]);

  // conditionally render a different icon depending on app state
  const renderAudioIcon = () => {
    if (audioLoading) {
      return <Loading small withOverlay={false} />;
    }
    if (audioPlaying && !audioPaused) {
      return <Pause size={20} />;
    }
    return <Play size={20} />;
  };

  if (audioError) {
    return (
      <Tile className="TextToSpeech__tile-skeleton">
        <div className="TextToSpeech__loading">
          <FormattedMessage {...messages.ttsAudioError} />
        </div>
      </Tile>
    );
  }

  // If no models or voices loaded return early
  if (models?.length === 0 && voices?.length === 0) {
    return null;
  }

  const voiceMeta = {
    description: selectedVoice?.labels?.description,
    friendly_name: selectedVoice?.name,
    gender: selectedVoice?.labels?.gender,
    language: selectedVoice?.fine_tuning?.language,
    name: selectedVoice?.name,
    customizable: false,
    supported_features: undefined,
    url: ''
  };

  return (
    <>
      <audio id="TextToSpeech__audio-11Labs" ref={audioRef} />
      <Tile className="TextToSpeech__tile">
        <TextToSpeechVoiceTile
          voiceModel={voiceMeta}
          expressive={false}
          modelName={selectedModel?.name}
          voiceName={selectedVoice?.name}
        />

        <div className="TextToSpeech__visualizer-container">
          <Button
            id="btn"
            className="TextToSpeech__visualizer-button"
            disabled={audioLoading}
            aria-label={t(messages.playAudioButtonAriaLabel)}
            onClick={playAudioDirect}
            size="field">
            {renderAudioIcon()}
          </Button>
          {audioRef.current && audioData && (
            <AudioWave
              audioRef={audioRef}
              audioWaveContainerRef={audioWaveContainerRef}
              data={audioData}
              duration={audioDuration}
            />
          )}
        </div>
        <TextArea
          id="tts-text"
          className="TextToSpeech__visualizer-text-area"
          labelText={t(messages.visualizerTextAreaLabel)}
          light
          maxLength={1000}
          onChange={e => setTextAreaValue(e.target.value)}
          placeholder={t(messages.visualizerTextAreaPlaceHolder)}
          value={textAreaValue}
        />
      </Tile>
    </>
  );
};
